import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { formatExpirationMonth } from "../lib/expiration";
import type { Apparatus, Bag, BagInventoryItem } from "../lib/types";
import { useRegisterViewRefresh } from "../lib/viewRefresh";

function scopeLabel(scope: Apparatus["compliance_scope"]) {
  return scope === "qrs" ? "QRS" : "Non-QRS";
}

function showBagInApparatusView(bagName: string) {
  const normalized = bagName.trim().toLowerCase();
  return !["extra", "rescue task force", "stop the bleed kit"].includes(normalized);
}

function bagLabel(name: string) {
  if (name.trim().toLowerCase() === "qrs bag") return "QRS Jump Kit";
  return name;
}

function useDebounced(value: string, ms = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

interface ApparatusSearchMatch {
  bagId: string;
  bagName: string;
  item: BagInventoryItem;
}

export function ApparatusSearchResults({
  searching,
  error,
  matches
}: {
  searching: boolean;
  error: string;
  matches: ApparatusSearchMatch[];
}) {
  return (
    <div className="card-soft stack-xs">
      <p className="small muted">Search results across all kits</p>
      {searching ? <p className="small muted">Searching...</p> : null}
      {error ? (
        <p className="error-text small" role="alert">
          {error}
          {matches.length > 0 ? " Previous results are still shown below." : ""}
        </p>
      ) : null}
      {!searching && !error && matches.length === 0 ? (
        <p className="small muted">No matching items found.</p>
      ) : null}
      {!searching && matches.length > 0 ? (
        <div className="list">
          {matches.slice(0, 60).map((match) => (
            <Link
              key={`${match.bagId}:${match.item.recordId ?? `missing:${match.item.catalogItemId}`}:${match.item.expirationDate ?? ""}`}
              to={`/bags/${match.bagId}`}
              className="list-item"
            >
              <div className="stack-xs">
                <strong className="text-wrap-anywhere">{match.item.itemName}</strong>
                <div className="row gap-sm search-result-meta">
                  <span className="kit-chip">{bagLabel(match.bagName)}</span>
                  <span className="muted small">
                    Qty {match.item.quantity}
                    {match.item.expirationDate ? ` · Exp ${formatExpirationMonth(match.item.expirationDate)}` : ""}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ApparatusScreen() {
  const { apparatusId = "" } = useParams();
  const [rows, setRows] = useState<Apparatus[]>([]);
  const [bagsByApparatus, setBagsByApparatus] = useState<Record<string, Bag[]>>({});
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchMatches, setSearchMatches] = useState<ApparatusSearchMatch[]>([]);
  const [searchError, setSearchError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const debouncedSearch = useDebounced(search.trim(), 250);
  const loadAbortRef = useRef<AbortController | null>(null);
  const searchAbortRef = useRef<AbortController | null>(null);
  const loadRequestIdRef = useRef(0);
  const loadedScopeRef = useRef<string | null>(null);

  const loadApparatus = async (throwOnFailure = false, forceRefresh = false) => {
    const requestId = ++loadRequestIdRef.current;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    setLoading(true);
    setError("");
    try {
      const apparatusResult = await api.getApparatusDirectory({
        signal: controller.signal,
        dedupe: false,
        forceRefresh
      });
      if (requestId !== loadRequestIdRef.current) return;
      const scopedRows = apparatusId
        ? apparatusResult.apparatus.filter((row) => row.id === apparatusId)
        : apparatusResult.apparatus;

      if (apparatusId && scopedRows.length === 0) {
        loadedScopeRef.current = apparatusId;
        setRows([]);
        setBagsByApparatus({});
        setError("Apparatus not found");
        return;
      }

      loadedScopeRef.current = apparatusId;
      setRows(scopedRows);
      setBagsByApparatus(Object.fromEntries(scopedRows.map((row) => [row.id, row.bags ?? []])));
    } catch (err: any) {
      if (requestId !== loadRequestIdRef.current) return;
      if (rows.length === 0) {
        setRows([]);
        setBagsByApparatus({});
      }
      setError(err?.message ?? "Failed to load apparatus");
      if (throwOnFailure) throw err;
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (loadedScopeRef.current != null && loadedScopeRef.current !== apparatusId) {
      setRows([]);
      setBagsByApparatus({});
    }
    loadApparatus().catch(() => {
      setLoading(false);
      setError("Failed to load apparatus");
    });
    return () => loadAbortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apparatusId]);

  useEffect(() => {
    setSearch("");
    setSearchMatches([]);
    setSearchError("");
  }, [apparatusId]);

  const currentApparatus = rows.length === 1 ? rows[0] : null;
  const visibleBags = useMemo(
    () => (currentApparatus ? (bagsByApparatus[currentApparatus.id] ?? []).filter((bag) => showBagInApparatusView(bag.name)) : []),
    [currentApparatus, bagsByApparatus]
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const currentSearch = search.trim();
      if (currentSearch !== debouncedSearch) {
        searchAbortRef.current?.abort();
        setSearching(currentSearch.length > 0);
        setSearchError("");
        if (!currentSearch) setSearchMatches([]);
        return;
      }

      if (!apparatusId || !currentApparatus || !debouncedSearch) {
        setSearching(false);
        setSearchMatches([]);
        setSearchError("");
        return;
      }

      if (visibleBags.length === 0) {
        setSearching(false);
        setSearchMatches([]);
        setSearchError("");
        return;
      }

      searchAbortRef.current?.abort();
      const controller = new AbortController();
      searchAbortRef.current = controller;
      setSearching(true);
      setSearchError("");
      try {
        const result = await api.searchApparatusInventory(apparatusId, debouncedSearch, 60, {
          signal: controller.signal,
          dedupe: false
        });

        if (cancelled) return;
        const flattened = (result.matches ?? []).sort((a, b) => {
          const bagCmp = bagLabel(a.bagName).localeCompare(bagLabel(b.bagName));
          if (bagCmp !== 0) return bagCmp;
          return a.item.itemName.localeCompare(b.item.itemName);
        });
        setSearchMatches(flattened);
      } catch (err: any) {
        if (!cancelled && !controller.signal.aborted) {
          setSearchError(
            err?.message ??
              "Could not search apparatus inventory. Check the connection and try again."
          );
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    };

    run().catch(() => undefined);

    return () => {
      cancelled = true;
      searchAbortRef.current?.abort();
    };
  }, [apparatusId, currentApparatus, search, debouncedSearch, visibleBags]);

  const matchingBagIds = useMemo(() => new Set(searchMatches.map((entry) => entry.bagId)), [searchMatches]);

  const bagFilterText = debouncedSearch.toLowerCase();

  useRegisterViewRefresh(() => loadApparatus(true, true), {
    disabled: loading,
    disabledReason: "Apparatus is already loading."
  });

  if (loading && rows.length === 0) return <p className="muted">Loading apparatus...</p>;
  if (error && rows.length === 0) {
    return (
      <section className="card stack-sm">
        <p className="error-text">{error}</p>
        <button className="btn btn-primary" onClick={() => loadApparatus()}>
          Retry
        </button>
      </section>
    );
  }

  return (
    <div className="stack-md">
      {error ? <p className="error-text">{error}</p> : null}
      {loading ? (
        <p className="muted small" role="status">
          Updating apparatus…
        </p>
      ) : null}
      {apparatusId ? (
        <section className="card-soft stack-xs">
          <Link className="inline-link" to="/apparatus">
            Back to all apparatus
          </Link>
        </section>
      ) : null}
      {rows.map((app) => (
        <section key={app.id} className="card stack-sm">
          <div className="row-between">
            <h2>
              {apparatusId ? (
                app.name
              ) : (
                <Link className="inline-link" to={`/apparatus/${app.id}`}>
                  {app.name}
                </Link>
              )}
            </h2>
            <span className="chip">{scopeLabel(app.compliance_scope)}</span>
          </div>
          {apparatusId ? (
            <div className="stack-xs">
              <input
                className="input"
                placeholder="Search all items and kits on this apparatus"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          ) : null}
          <div className="bag-grid">
            {(bagsByApparatus[app.id] ?? [])
              .filter((bag) => showBagInApparatusView(bag.name))
              .filter((bag) => {
                if (!apparatusId || !debouncedSearch) return true;
                const bagNameMatch = bagLabel(bag.name).toLowerCase().includes(bagFilterText);
                return bagNameMatch || matchingBagIds.has(bag.id);
              })
              .map((bag) => (
                <Link key={bag.id} to={`/bags/${bag.id}`} className="bag-tile">
                  <span className="text-wrap-anywhere">{bagLabel(bag.name)}</span>
                </Link>
              ))}
          </div>
          {apparatusId && debouncedSearch ? (
            <ApparatusSearchResults
              searching={searching}
              error={searchError}
              matches={searchMatches}
            />
          ) : null}
        </section>
      ))}
    </div>
  );
}
